import { NodeInstanceDataPort } from '@glueapp/graph-types';
import { createNode } from '@glueapp/graph-types';

const MatcherPortTemplate: NodeInstanceDataPort = { name: 'Matcher for Case', type: { kind: 'generic', identifier: 'T' } };
const ValuePortTemplate: NodeInstanceDataPort = { name: 'Value for Case', type: { kind: 'generic', identifier: 'U' } };

// Helper functions for port matching (moved from useDuplicatablePortList)
export const doesPortMatchTemplate = (portName: string, template: NodeInstanceDataPort): boolean => {
    return portName.startsWith(template.name);
};

export const getPortNameByTemplate = (portName: string, template: NodeInstanceDataPort): string | undefined => {
    const portGroupNumberMatch = portName.match(/([0-9]+)$/);
    return portGroupNumberMatch ? `${template.name} ${portGroupNumberMatch[1]}` : undefined;
};

export const Switch = createNode({
    id: 'Switch',
    name: 'Switch',
    description: 'A Node for conditionally selecting a value',
    aiDescription:
        'Pattern-matches an input value against multiple cases and returns the corresponding result (like a switch statement).\n' +
        'Configure cases headlessly: set state `duplicatedPortGroupsValues` to an array of case numbers (e.g. [1, 2, 3]) — each N adds paired "Matcher for Case N" / "Value for Case N" input ports. (The switch-editor UI sets the same state.)\n' +
        'Connect the value to match against to "Input" and a fallback to "Default".\n' +
        'For each case, connect the pattern to "Matcher for Case N" and the result to "Value for Case N".\n' +
        'The first matcher that equals Input wins; if none match, "Default" is used.\n' +
        'Output: single "Result" port with the matched value.',
    category: 'Flow Control',
    color: '#869094',
    integration: undefined,
    inPorts: {
        Input: {
            kind: 'generic',
            identifier: 'T',
        },
        Default: {
            kind: 'generic',
            identifier: 'U',
        },
    },
    outPorts: {
        Result: {
            kind: 'generic',
            identifier: 'U',
        },
    },
    interfaceComponentId: 'switch-editor',
    computeShape: input => {
        const portGroupNumbers = (input.state?.duplicatedPortGroupsValues as number[]) || [];
        const dynamicPorts = portGroupNumbers.flatMap(num => [
            { name: `Matcher for Case ${num}`, type: { kind: 'generic' as const, identifier: 'T' } },
            { name: `Value for Case ${num}`, type: { kind: 'generic' as const, identifier: 'U' } },
        ]);
        return {
            inPorts: [...input.nodeInPorts, ...dynamicPorts],
            outPorts: input.nodeOutPorts,
        };
    },
    execute: function (input) {
        const inputValue = input.inPorts.Input;

        for (let inPortName in input.inPorts) {
            if (doesPortMatchTemplate(inPortName, MatcherPortTemplate)) {
                // @ts-expect-error
                if (input.inPorts[inPortName] === inputValue) {
                    const valuePortName = getPortNameByTemplate(inPortName, ValuePortTemplate);
                    // @ts-expect-error
                    return { output: { Result: input.inPorts[valuePortName] } };
                }
            }
        }

        return { output: { Result: input.inPorts.Default } };
    },
});
